/* eslint-disable import/no-extraneous-dependencies */
import React, {
  Fragment,
  useState,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import universal from '@nerdwallet/coin/universal';
import LazyHydrate from './lazy-hydrate';

export enum Hydrate {
  SSR_ONLY = 1,
  WHEN_VISIBLE = 2,
  WHEN_IDLE = 3,
  WHEN_LOAD = 4, // hydrate as soon the chunk is loaded
  IMMEDIATE = 5, // do not lazy hydrate (can cause layout shift)
  CONTROLLED = 6, // controlled hydration by using ref
}

export type LazyHydrateUniversalProps = {
  children: (any?) => JSX.Element;
  hydrate?: Hydrate;
  hydrateOn?: (keyof HTMLElementEventMap)[] | keyof HTMLElementEventMap;
  hydrateRef: React.ForwardedRef<any>;
};

/**
 * Component that ensures the lazy component is rendered on the client right after
 * the corresponding chunk is loaded to avoid flickering and layout shifts.
 */
const LazyHydrateUniversal = ({
  children,
  hydrate = Hydrate.WHEN_LOAD,
  hydrateOn,
  hydrateRef,
}: LazyHydrateUniversalProps): JSX.Element => {
  const [loaded, setLoaded] = useState(false);
  const wrapperRef = useRef();
  const handleAfter = useCallback(({ isServer }) => {
    if (!isServer) setLoaded(true);
  }, []);
  // if the chunk is loaded or if we don't want to wait we are good to render on the client
  if (loaded || hydrate === Hydrate.IMMEDIATE) return children();
  // ssr only - won't trigger chunk loading
  if (hydrate === Hydrate.SSR_ONLY)
    return <LazyHydrate ssrOnly>{children()}</LazyHydrate>;
  // adding props to the Universal component so we can track using the onAfter callback when the chunk is loaded
  const loadTrigger = children({
    onAfter: handleAfter,
    trigger: true,
  });
  const hydrateOnLoad = hydrate === Hydrate.WHEN_LOAD && !hydrateOn;
  return (
    <Fragment>
      {!hydrateOnLoad && (
        <LazyHydrate
          whenVisible={hydrate === Hydrate.WHEN_VISIBLE && !hydrateOn}
          whenIdle={hydrate === Hydrate.WHEN_IDLE && !hydrateOn}
          controlled={hydrate === Hydrate.CONTROLLED}
          on={hydrateOn}
          ref={hydrateRef}
          rootElement={wrapperRef.current}
        >
          {loadTrigger}
        </LazyHydrate>
      )}
      {hydrateOnLoad && loadTrigger}
      <div ref={wrapperRef}>
        <LazyHydrate ssrOnly>{children()}</LazyHydrate>
      </div>
    </Fragment>
  );
};

/**
 * Extending the Universal package to bring together code split + lazy hydration.
 * Creates a HOC around LazyHydrate and Universal components to control the process
 * between chunk loading and lazy hydration.
 * @param asyncComponent
 * @param options
 * @returns LazyUniversal component
 */
function lazyUniversal<T = any>(
  asyncComponent: () => Promise<any>,
  {
    hydrate = Hydrate.WHEN_LOAD,
    hydrateOn = undefined,
    ...universalOptions
  } = {}
): React.FC<T> {
  const Universal = universal(asyncComponent, {
    loadingTransition: false,
    render: ({ trigger, ...props }, Component): JSX.Element => {
      // If the Component is empty and this is not the load trigger this means that we
      // don't have the corresponding chunk loaded yet. This shouldn't happen if we are
      // using lazy hydration (it should happen only for hydrate === Hydrate.IMMEDIATE)
      if (!Component || trigger) {
        return <div />;
      }
      return <Component {...props} />;
    },
    ...universalOptions,
  });

  const LazyUniversal = (
    props: any,
    ref: React.ForwardedRef<any>
  ): JSX.Element => {
    const hydrateRef = useRef();
    // allow controlled hydration by using refs
    useImperativeHandle(ref, () => ({
      // @ts-ignore possible undefined
      hydrate: hydrateRef?.current?.hydrate,
    }));
    return (
      <LazyHydrateUniversal
        hydrate={hydrate}
        hydrateOn={hydrateOn}
        hydrateRef={hydrateRef}
      >
        {(universalProps) => <Universal {...universalProps} {...props} />}
      </LazyHydrateUniversal>
    );
  };
  const ForwardedRefLazyUniversal = forwardRef(LazyUniversal);
  // expose statics when SSR only (e.g. allow fragments to be used on the server)
  if (typeof window === 'undefined' && hydrate === Hydrate.SSR_ONLY) {
    const Component = Universal.preloadWeak();
    // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
    const hoistNonReactStatics = require('hoist-non-react-statics');
    hoistNonReactStatics(ForwardedRefLazyUniversal, Component);
  }
  return ForwardedRefLazyUniversal;
}
export default lazyUniversal;
